<template>
  <div :id="rootElementId" :style="`height:${PITCH_HEIGHT}px`"></div>
</template>

<script>
import * as _ from "lodash";
import { extent, sum } from "d3-array";
import { getDataUrl } from "../../utils/functions";
import { loadFonts, copyAndAppendElement } from "../../utils/exportToPNG";
import ResizeObserver from 'resize-observer-polyfill';

export default {
  name: "PassMapChart",
  props: {
    id:{
      type: String,
      required: true
    },
    size: {
      type: Number,
      required: false
    },
    margin: {
      type: Object,
      required: false
    },
    passes: {
      type: Array,
      required: false
    },
    passesByPlayersName: {
      type: Object,
      required: false
    },
    playersAvgPosition: {
      type: Array,
      required: false
    },
    teamId: {
      type: Number,
      required: false
    },
    teamFormation: {
      type: String,
      required: false
    },
    teamColor: {
      type: String,
      required: false
    },
    strokeColor: {
      type: String,
      required: false
    },
    teamName: {
      type: String,
      required: false
    },
    minFrom: {
      type: Number,
      required: false
    },
    minTo: {
      type: Number,
      required: false
    },
    language: {
      type: String,
      required: false
    },
    mediaMode: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    SVG_WIDTH() {
      return this.size ? this.size : 380;
    },
    PITCH_WIDTH() {
      if (this.margin) {
        return this.SVG_WIDTH - this.margin.left - this.margin.right;
      } else {
        return this.SVG_WIDTH;
      }
    },
    PITCH_HEIGHT() {
      return this.PITCH_WIDTH * 0.67125;
    },
    rootElementId() {
      return `pitchPassMap${this.id}`;
    },
    currentUserFieldColor() {
      return this.$store.getters["auth/fieldColor"];
    }
  },
  data() {
    return {
      height: 0,
      width: 0
    };
  },
  async mounted() {
    this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));

    const SVG_WIDTH = this.size ? this.size : 380;
    const isMobileDevice = SVG_WIDTH < 380 ? 1 : 0;
    const margin = this.margin
      ? this.margin
      : { top: 0, right: 0, bottom: 0, left: 0 };
    const PITCH_WIDTH = SVG_WIDTH - margin.left - margin.right;
    const PITCH_HEIGHT = PITCH_WIDTH * 0.67125;
    const CHART_SIZE_RATIO =
      SVG_WIDTH < 380 ? (SVG_WIDTH / 2) * 0.0026 : (SVG_WIDTH / 2) * 0.0022; //CHART_SIZE

    d3.selectAll(".tooltip").remove();

    const tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "tooltip");

    var svg = d3
      .select(`#${this.rootElementId}`)
      .append("svg")
      .attr("width", PITCH_WIDTH)
      .attr("height", PITCH_HEIGHT)
      .attr("id",`${this.rootElementId}SVG`)
      .style("background-color", "#ffffff");

    //Render Green Pitch
    this.drawImageBase64(
      svg,
      margin.left,
      margin.top,
      PITCH_WIDTH,
      PITCH_HEIGHT,
      require(`@/assets/img/pitch_${this.currentUserFieldColor}.png`),
      true
    );

    let passesByPlayer = _.chain(this.passes)
      .groupBy("playerFromId")
      .map((passesGrouped, id) => {
        let pass = _.first(passesGrouped);
        let x = _.get(
          this.playersAvgPosition.filter(p => p.playerId == id),
          "[0].x"
        );
        let y = _.get(
          this.playersAvgPosition.filter(p => p.playerId == id),
          "[0].y"
        );

        let countPasses = _.chain(this.passes)
          .filter(p => p.playerFromId == id)
          .size()
          .value();
        let name = pass.playerFromName;

        let r = countPasses / 50;
        let opacity = 0.5;
        return { x, y, countPasses, name, r, opacity };
      })
      .value();

    //Unify passes (Just count as 1 the pass between player A and player B)
    let passesUnified = this.unifyPasses(this.passesByPlayersName);

    let segments = this.computePassLines(passesByPlayer, passesUnified);

    this.printPassMapTextsLegendAndLogo(
      svg,
      PITCH_WIDTH,
      PITCH_HEIGHT,
      margin,
      this.teamId,
      CHART_SIZE_RATIO
    );
    this.renderSegments(
      svg,
      PITCH_WIDTH,
      PITCH_HEIGHT,
      segments,
      this.teamColor,
      isMobileDevice,
      CHART_SIZE_RATIO
    );
    this.renderCircles(
      svg,
      PITCH_WIDTH,
      PITCH_HEIGHT,
      passesByPlayer,
      this.teamColor,
      this.strokeColor,
      CHART_SIZE_RATIO,
      tooltip,
      isMobileDevice,
      this.language
    );
    let font = await loadFonts();
    copyAndAppendElement(svg.node(), font.node(), 0, 0);

    //Emit when is loaded
    this.$emit("loaded", this.rootElementId);
  },
  methods: {
    renderCircles(
      svg,
      chartWidth,
      chartHeight,
      players,
      teamColor,
      strokeColor,
      chartSizeRatio,
      tooltip,
      isMobileDevice,
      language
    ) {
      let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
      // Remove DUPS
      const circles = [
        ...new Map(players.map(obj => [JSON.stringify(obj), obj])).values()
      ];

      let x = d3.scale
        .linear()
        .domain([0 - 100 * 0.093, 100 + 100 * 0.098])
        .range([chartWidth, 0]);
      let y = d3.scale
        .linear()
        .domain([0 - 100 * 0.098, 100 + 100 * 0.098])
        .range([chartHeight, 0]);
      //let radius = d3.scale.linear().domain(radiusDomain).range([4, 75]);
      let radius = d3.scale
        .linear()
        .domain([0, 1])
        .range([chartWidth * 0.0055, chartHeight * 0.024]);

      for (let circle of circles) {
        if (!circle.x || !circle.y) {
          console.log("Player not found");
        } else {
          let sx = x(100 - circle.x) + isMobileDevice * 9.5;
          let sy = y(circle.y);
          //Draw circles
          this.drawCircle(
            svg,
            sx,
            sy,
            circle,
            radius(circle.r) / chartSizeRatio,
            teamColor,
            "#000000",
            chartWidth,
            chartSizeRatio,
            tooltip,
            isMobileDevice,
            language
          );
          const yCoord = sy - 15 - radius(circle.r) + isMobileDevice * 8;
          const xCoord =
            sx - this.measureText(circle.name, 14 * chartSizeRatio) / 2;

          //Write players name over circles
          this.writeText(
            svg,
            xCoord,
            yCoord,
            "center",
            17 * chartSizeRatio + "px",
            circle.name,
            textColor
          );
        }
      }
    },
    renderSegments(
      svg,
      chartWidth,
      chartHeight,
      segments,
      teamColor,
      isMobileDevice,
      chartSizeRatio,
      widthDomain = null
    ) {
      widthDomain =
        widthDomain !== null ? [0, 60] : extent(segments, c => c.width);
      let x = d3.scale
        .linear()
        .domain([0 - 100 * 0.093, 100 + 100 * 0.098])
        .range([chartWidth, 0]);
      let y = d3.scale
        .linear()
        .domain([0 - 100 * 0.098, 100 + 100 * 0.098])
        .range([chartHeight, 0]);
      let width = d3.scale
        .linear()
        .domain(widthDomain)
        .range([1, 10]);
      // ====================================================================
      //        Render Segments
      // ====================================================================

      for (let segment of segments.filter(
        p => p.x0 !== null && p.y0 !== null && p.x1 !== null && p.y1 !== null
      )) {
        if (segment.x1 === null)
          console.log("NULL SEGMENT " + JSON.stringify(segment));
        let sx0 = x(100 - segment.x0) + isMobileDevice * 9.5;
        let sy0 = y(segment.y0);
        let sx1 = x(100 - segment.x1) + isMobileDevice * 9.5;
        let sy1 = y(segment.y1);
        this.drawLine(
          svg,
          sx0,
          sy0,
          sx1,
          sy1,
          width(segment.width) * chartSizeRatio,
          teamColor,
          0.4
        );
      }
    },
    drawLine(svg, x0, y0, x1, y1, width, strokeColor, opacity = 1) {
      svg
        .append("line")
        .attr("x1", x0)
        .attr("y1", y0)
        .attr("x2", x1)
        .attr("y2", y1)
        .attr("stroke-width", width)
        .attr("stroke-linecap", "round")
        .style("opacity", opacity)
        .attr(
          "stroke",
          strokeColor ? strokeColor.replace("0x", "#") : "#0000FF"
        );
    },
    drawCircle(
      svg,
      x,
      y,
      circle,
      radius,
      teamColor,
      strokeColor,
      chartWidth,
      chartSizeRatio,
      tooltip,
      isMobileDevice,
      language
    ) {
      var self = this;
      svg
        .append("circle")
        .attr("r", radius * chartSizeRatio)
        .attr("cx", x)
        .attr("cy", y)
        .attr("id", "pass-" + circle.name + `-${this.rootElementId}`)
        .style("fill-opacity", 0.8)
        .style("opacity", 0.8)
        .style("stroke", strokeColor)
        .style("stroke-width", 1.5 * chartSizeRatio)
        .style("fill", teamColor ? teamColor.replace("0x", "#") : "#0000FF")
        .on("mouseenter", function(d) {
          d3.select(this)
            .transition()
            .duration(300)
            .attr("stroke-width", 3)
            .attr("r", d => radius * 1.3 * chartSizeRatio);

          let passesId = document.getElementById(
            "pass-" + circle.name + `-${self.rootElementId}`
          );
          let tooltipText =
            language == "en" ? " Successful Passes" : " Pases Completados";
          let maxTextWidth = self.measureText(
            circle.name + " • " + circle.countPasses + tooltipText,
            16 * chartSizeRatio
          );
          let widthTooltip =
            chartSizeRatio > 0.5 ? maxTextWidth + 75 : maxTextWidth + 40;
          let height =
            chartSizeRatio > 0.5 ? 50 * chartSizeRatio : 70 * chartSizeRatio;
          let heightTooltip =
            chartWidth * 0.05 + isMobileDevice * 2.5 + 16 * chartSizeRatio;

          let topOffset = 110 * chartSizeRatio;
          let leftOffset =
            chartSizeRatio > 0.5
              ? Math.ceil(passesId.getAttribute("r"))
              : Math.ceil(passesId.getAttribute("r")) - 0.5;

          d3.select("body > .tooltip")
            .style("width", widthTooltip + "px")
            .style("height", heightTooltip + "px")
            .style("font-size", 16 * chartSizeRatio + "px")
            .style("padding-top", 10 * chartSizeRatio + "px")
            .style(
              "left",
              passesId.getBoundingClientRect().left -
                widthTooltip / 2 +
                leftOffset +
                "px"
            )
            .style("top", event.pageY - topOffset + "px")
            .style("display", "inline-block")
            .html(
              "<b>" +
                circle.name +
                "</b></br> " +
                circle.countPasses +
                " Successful Passes"
            );
        })
        .on("mouseout", function() {
          d3.select("body > .tooltip").style("display", "none");
          d3.select(this)
            .transition()
            .duration(300)
            .attr("r", d => radius * chartSizeRatio);
        });
    },
    computePassLines(players, passes) {
      let passLines = [];
      let playersByName = players.reduce((acc, p) => {
        acc[p.name] = p;
        return acc;
      }, {});
      for (let player of players) {
        const fromP = player.name;
        if (fromP in passes) {
          for (let toP in passes[fromP]) {
            let count = passes[fromP][toP];
            if (!(toP in playersByName)) continue;

            if (playersByName[toP].x !== null && playersByName[toP].y)
              passLines.push({
                x0: player.x,
                y0: player.y,
                x1: playersByName[toP].x,
                y1: playersByName[toP].y,
                width: count
              });
          }
        } else {
          console.error(fromP, "not in", passes);
        }
      }

      return passLines;
    },
    unifyPasses(passes) {
      let unifiedPasses = {};
      for (let fromP in passes) {
        for (let toP in passes[fromP]) {
          if (
            toP in unifiedPasses &&
            unifiedPasses[toP] &&
            fromP in unifiedPasses[toP]
          ) {
            unifiedPasses[toP][fromP] += passes[toP][fromP];
          } else {
            if (!(fromP in unifiedPasses)) {
              unifiedPasses[fromP] = {};
            }
            if (!(toP in unifiedPasses[fromP])) {
              unifiedPasses[fromP][toP] = passes[fromP][toP];
            } else {
              unifiedPasses[fromP][toP] += passes[fromP][toP];
            }
          }
        }
      }
      return unifiedPasses;
    },
    printPassMapTextsLegendAndLogo(
      svg,
      pitchSize,
      pitchHeight,
      margin,
      teamId
    ) {
      let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
      //Render team crest
      this.drawImageBase64(
        svg,
        0.07 * pitchSize + margin.left,
        0.03 * pitchHeight + margin.top,
        0.05 * pitchSize,
        0.05 * pitchHeight,
        "https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/" +
          teamId +
          ".png"
      );

      //Render team name and team formation
      this.writeText(
        svg,
        0.115 * pitchSize + margin.left,
        0.04 * pitchHeight + margin.top,
        "start",
        0.022 * pitchSize + "px",
        this.teamName + " • " + this.teamFormation,textColor
      );
      //Render total number of passes
      let text =
        this.language == "en"
          ? this.passes.length +
            " passes from " +
            this.minFrom +
            "' to " +
            this.minTo +
            "'"
          : this.passes.length +
            " pases del " +
            this.minFrom +
            "' al " +
            this.minTo +
            "'";
      this.writeText(
        svg,
        0.925 * pitchSize + margin.left,
        0.04 * pitchHeight + margin.top,
        "end",
        0.02 * pitchSize + "px",
        text,textColor
      );

      //Render bottom legend (touches, passes, player location)
      let legendImage = require(`@/assets/img/legend_${this.language === 'en' ? 'english' : 'spanish'}${this.currentUserFieldColor === 'white' ? "_dark": ""}.png`);
      this.drawImageBase64(
        svg,
        pitchSize * 0.08 + margin.left,
        pitchHeight * 0.9 + margin.top,
        pitchSize * 0.44,
        pitchHeight * 0.1,
        legendImage
      );
      //Render bottom legend (Attacking direction)
      let attackLegendImage = require(`@/assets/img/attack_${this.language === 'en' ? 'english' : 'spanish'}${this.currentUserFieldColor === 'white' ? "_dark": ""}.png`);
      this.drawImageBase64(
        svg,
        pitchSize * 0.71 + margin.left,
        pitchHeight * 0.9 + margin.top,
        pitchSize * 0.225,
        pitchHeight * 0.084,
        attackLegendImage
      );
      //Render Dark Driblab logo
      let logo  = require(`@/assets/img/driblab${this.mediaMode ? "_normal" : ""}_${this.currentUserFieldColor === 'white' ? 'color' : 'white'}.png`);
      this.drawImageBase64(
        svg,
        pitchSize * 0.735 + margin.left,
        pitchHeight * 0.77 + margin.top,
        pitchSize * 0.18,
        pitchHeight * 0.18,
        logo
      );
    },
    writeText(
      svg,
      x,
      y,
      anchor,
      fontSize,
      text,
      fill = "white",
      className = "none"
    ) {
      svg
        .append("text")
        .attr("x", x)
        .attr("y", y)
        .attr("text-anchor", anchor)
        .attr("dy", "0.8em")
        .attr("font-weight", 500)
        .attr("class", className)
        .style("font-size", fontSize)
        .style("font-family", "NeueMontreal")
        .style("fill", fill)
        .text(text);
    },
    drawImageBase64(svg, x, y, width, height, img, bottom = false) {
      let base_image = new Image();
      base_image.crossOrigin = "anonymous";
      base_image.src = img;
      base_image.onload = event => {
        const dataUrl = getDataUrl(event.currentTarget);
        this.drawImage(svg, x, y, width, height, dataUrl, bottom);
      };
    },
    drawImage: function(svg, x, y, width, height, img, bottom = false) {
      let imgNode = bottom
        ? svg.insert("svg:image", ":first-child")
        : svg.append("svg:image");
      imgNode
        .attr("x", x)
        .attr("y", y)
        .attr("width", width)
        .attr("height", height)
        .attr("xlink:href", img);
    },
    measureText(string, fontSize = 10) {
      const widths = [
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0.278125,
        0.278125,
        0.35625,
        0.55625,
        0.55625,
        0.890625,
        0.6671875,
        0.1921875,
        0.334375,
        0.334375,
        0.390625,
        0.584375,
        0.278125,
        0.334375,
        0.278125,
        0.303125,
        0.55625,
        0.55625,
        0.55625,
        0.55625,
        0.55625,
        0.55625,
        0.55625,
        0.55625,
        0.55625,
        0.55625,
        0.278125,
        0.278125,
        0.5859375,
        0.584375,
        0.5859375,
        0.55625,
        1.015625,
        0.6671875,
        0.6671875,
        0.7234375,
        0.7234375,
        0.6671875,
        0.6109375,
        0.778125,
        0.7234375,
        0.278125,
        0.5,
        0.6671875,
        0.55625,
        0.834375,
        0.7234375,
        0.778125,
        0.6671875,
        0.778125,
        0.7234375,
        0.6671875,
        0.6109375,
        0.7234375,
        0.6671875,
        0.9453125,
        0.6671875,
        0.6671875,
        0.6109375,
        0.278125,
        0.35625,
        0.278125,
        0.478125,
        0.55625,
        0.334375,
        0.55625,
        0.55625,
        0.5,
        0.55625,
        0.55625,
        0.278125,
        0.55625,
        0.55625,
        0.2234375,
        0.2421875,
        0.5,
        0.2234375,
        0.834375,
        0.55625,
        0.55625,
        0.55625,
        0.55625,
        0.334375,
        0.5,
        0.278125,
        0.55625,
        0.5,
        0.7234375,
        0.5,
        0.5,
        0.5,
        0.35625,
        0.2609375,
        0.3546875,
        0.590625
      ];
      const avg = 0.5293256578947368;

      return (
        string
          .split("")
          .map(c =>
            c.charCodeAt(0) < widths.length ? widths[c.charCodeAt(0)] : avg
          )
          .reduce((cur, acc) => acc + cur) * fontSize
      );
    },
    onResize(){
      let elto =document.getElementById(this.rootElementId);
      if(elto){
        this.$emit("sizeChange", {height:elto.clientHeight,width:elto.clientWidth});
      }
    }
  },
  destroyed() {
    delete this.observer;
  },
};
</script>



// WEBPACK FOOTER //
// src/views/base/PassMapChart.vue